import { uiEnvironment } from 'apps/sigma2-ui/src/constants/environment';
import { useCallback } from 'react';
import { useEffect } from 'react';
import { useMemo } from 'react';
import mixpanel from 'mixpanel-browser';
import { MyOrganization } from 'libs/end-to-end/src/my-organization';
import { MyUser } from 'libs/end-to-end/src/my-user';
import { AnalyticsEvent } from 'libs/end-to-end/src/analytics/AnalyticsEvent';

export interface UseAnalyticsProviderPayload {
  trackEvent: (event: AnalyticsEvent) => void;
  login: (user: MyUser) => void;
  registerOrganization: (organization: MyOrganization) => void;
  registerDatabase: ({
    databaseId,
    databaseName,
    databaseRevisionId,
  }: {
    databaseId: string;
    databaseName: string;
    databaseRevisionId: string;
  }) => void;
  logout: () => void;
}

const useMixpanelAnalytics = (): UseAnalyticsProviderPayload => {
  const isEnabled = uiEnvironment.mixpanelEnabled;

  const mixpanelBaseUrl = uiEnvironment.mixpanelBaseUrl;
  const mixpanelApiKey = uiEnvironment.mixpanelApiKey;

  const enabledCredentials: {
    baseUrl: string;
    apiKey: string;
  } | null = useMemo(() => {
    if (uiEnvironment.mixpanelEnabled && mixpanelBaseUrl && mixpanelApiKey) {
      return {
        baseUrl: mixpanelBaseUrl,
        apiKey: mixpanelApiKey,
      };
    } else {
      return null;
    }
  }, [mixpanelBaseUrl, mixpanelApiKey]);

  useEffect(() => {
    if (enabledCredentials) {
      const config = {
        api_host: enabledCredentials.baseUrl,
        record_sessions_percent: 100,
        record_mask_text_selector: '.dont-record', // sensitive text that shouldn't be recorded
        autocapture: true,
      } as any; // TODO: autocapture is not added yet to @types/mixpanel-browser
      mixpanel.init(enabledCredentials.apiKey, config);
    }
  }, [enabledCredentials]);

  const trackEvent = useCallback(
    (event: AnalyticsEvent) => {
      if (enabledCredentials) {
        mixpanel.track(event.eventName, event.eventProperties);
      }
    },
    [enabledCredentials],
  );

  const login = useCallback(
    (user: MyUser) => {
      if (enabledCredentials) {
        mixpanel.identify(user.id);
        mixpanel.people.set({
          $email: user.email,
          'Is Super Admin': user.roleAssignments === 'superAdmin',
        });
      }
    },
    [enabledCredentials],
  );

  const logout = useCallback(() => {
    if (enabledCredentials) {
      mixpanel.reset();
    }
  }, [enabledCredentials]);

  const registerOrganization = useCallback(
    (organization: MyOrganization) => {
      if (enabledCredentials) {
        mixpanel.register({
          'Selected Organization Id': organization.id,
          'Selected Organization Name': organization.name,
        });
      }
    },
    [enabledCredentials],
  );

  const registerDatabase = useCallback(
    ({
      databaseId,
      databaseName,
      databaseRevisionId,
    }: {
      databaseId: string;
      databaseName: string;
      databaseRevisionId: string;
    }) => {
      if (enabledCredentials) {
        mixpanel.register({
          'Selected Database Id': databaseId,
          'Selected Database Name': databaseName,
          'Selected Database Revision Id': databaseRevisionId,
        });
      }
    },
    [enabledCredentials],
  );

  return useMemo(() => {
    return {
      isEnabled,
      trackEvent,
      registerOrganization,
      registerDatabase,
      login,
      logout,
    };
  }, [
    isEnabled,
    login,
    logout,
    registerDatabase,
    registerOrganization,
    trackEvent,
  ]);
};

export interface UseAnalyticsPayload {
  trackEvent: (event: AnalyticsEvent) => void;
  login: (user: MyUser) => void;
  logout: () => void;
  registerOrganization: (organization: MyOrganization) => void;
  registerDatabase: ({
    databaseId,
    databaseName,
    databaseRevisionId,
  }: {
    databaseId: string;
    databaseName: string;
    databaseRevisionId: string;
  }) => void;
}
export const useAnalytics = (): UseAnalyticsPayload => {
  const mixpanel = useMixpanelAnalytics();

  const trackEvent = useCallback(
    (event: AnalyticsEvent) => {
      mixpanel.trackEvent(event);
    },
    [mixpanel],
  );

  const login = useCallback(
    (user: MyUser) => {
      mixpanel.login(user);
    },
    [mixpanel],
  );

  const logout = useCallback(() => {
    mixpanel.logout();
  }, [mixpanel]);

  const registerOrganization = useCallback(
    (organization: MyOrganization) => {
      mixpanel.registerOrganization(organization);
    },
    [mixpanel],
  );

  const registerDatabase = useCallback(
    ({
      databaseId,
      databaseName,
      databaseRevisionId,
    }: {
      databaseId: string;
      databaseName: string;
      databaseRevisionId: string;
    }) => {
      mixpanel.registerDatabase({
        databaseId,
        databaseName,
        databaseRevisionId,
      });
    },
    [mixpanel],
  );

  return useMemo(() => {
    return {
      trackEvent,
      login,
      logout,
      registerOrganization,
      registerDatabase,
    };
  }, [login, logout, registerDatabase, registerOrganization, trackEvent]);
};
